import { useMemo } from 'react';
import Image from 'next/image';
import Link from 'next/link';

import { Box, Flex, Heading, SimpleGrid, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { ArrowRightIcon } from '~/components/icons';
import { trackEvent } from '~/features/analytics/track-event';
import { getSchoolShortName } from '~/features/program/utils/get-school-data';
import { SCHOOLS } from '~/features/school/constants';
import { useI18n } from '~/hooks/use-i18n';

import { SCHOOL_LIBRARY_SLUG_ORDER, SCHOOL_LP_ANALYTICS_EVENT } from '../constants';
import { SchoolLpContent } from '../models/school-lp-content';

type SchoolLibrarySectionProps = NonNullable<StaticSchoolLibrarySectionProps>;

export function SchoolLibrarySection({ title, schools }: SchoolLibrarySectionProps) {
  const { t, i18n } = useI18n();
  const sortedSchools = useMemo(
    () => sortSchoolsByShortName(schools, (slug) => getSchoolShortName(slug, t, i18n.language)),
    [schools, t, i18n.language]
  );

  return (
    <Box as='section' bg='grey.50' padding={{ base: '64px 0', md: '96px 0' }}>
      <ContentContainer contentMaxWidth='content-max-width-md'>
        <Flex alignItems='center' flexDirection='column' gap={{ base: '32px', md: '48px' }}>
          <Heading as='h2' color='grey.900' size={{ base: 'mobile-heading-2', md: 'heading-2' }} textAlign='center'>
            {title}
          </Heading>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing='16px' width='100%'>
            {sortedSchools.map((school) => (
              <Flex
                key={school.slug}
                _hover={{ boxShadow: '0px 0px 12px 0px #00000014' }}
                alignItems='center'
                as={Link}
                bg='white'
                borderRadius='4px'
                href={`/school/${school.slug}`}
                justifyContent='space-between'
                paddingLeft='20px'
                paddingRight='26px'
                paddingY='18px'
                onClick={() =>
                  trackEvent({
                    name: SCHOOL_LP_ANALYTICS_EVENT,
                    type: 'click',
                    label: 'school_library_clicked',
                    additionalAttributes: { school: school.slug },
                  })
                }
              >
                <Flex alignItems='center' gap='12px'>
                  <Image alt='' height={20} quality={100} src={school.iconPath} width={18} />
                  <Text color='grey.500' fontSize='1rem' fontWeight='600' lineHeight='1.5'>
                    {getSchoolShortName(school.slug, t, i18n.language)}
                  </Text>
                </Flex>
                <ArrowRightIcon color='grey.300' h='16px' w='16px' />
              </Flex>
            ))}
          </SimpleGrid>
        </Flex>
      </ContentContainer>
    </Box>
  );
}

/** Alphabetical by localized display name; `SCHOOL_LIBRARY_SLUG_ORDER` only defines membership. */
export function sortSchoolsByShortName<T extends { slug: string }>(
  schools: T[],
  getName: (slug: string) => string
): T[] {
  return [...schools].sort((a, b) => getName(a.slug).localeCompare(getName(b.slug)));
}

export function transformStaticSchoolLibrarySectionProps({
  content,
  currentSchoolSlug,
}: {
  content: SchoolLpContent;
  /** Slug of the school page being rendered, excluded so the grid doesn't link to itself. */
  currentSchoolSlug?: string;
}) {
  if (!content.schoolLibrary) {
    return null;
  }

  const schoolsBySlug = new Map(SCHOOLS.map((school) => [school.slug, school]));

  return {
    title: content.schoolLibrary.title,
    schools: SCHOOL_LIBRARY_SLUG_ORDER.filter((slug) => slug !== currentSchoolSlug)
      .map((slug) => schoolsBySlug.get(slug))
      .filter((school): school is NonNullable<typeof school> => Boolean(school)),
  };
}

export type StaticSchoolLibrarySectionProps = ReturnType<typeof transformStaticSchoolLibrarySectionProps>;
